import { useEffect, useRef } from "react";
import { useToast } from 'alm-react-components';
import { useTranslation } from 'react-i18next';
import { getCorporateAccountStatus } from '../services/user';
import { clearLocalStorage, removeChainlitPopup } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { constants, StatusCode } from "../assets/constants/constants";

const useAlmCorporateStatus = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleAccountDeactivation = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    toast({
      description: t("Common.accountDeactivatedMessage"),
      variant: "error",
    });

    timeoutRef.current = setTimeout(() => {
      removeChainlitPopup()
      clearLocalStorage();
      navigate("/");
    }, constants.STATUS_CHECK_TIME);
  };

  const checkStatus = async () => {
    try {
      const response = await getCorporateAccountStatus();

      if (response === Number(StatusCode.SUCCESS)) {
        return;
      }
    } catch (error: unknown) {
      console.error("Error checking corporate account status:", error);

      // Type narrowing for safety
      if (typeof error === "object" && error !== null) {
        const err = error as {
          code?: string;
          status?: string | number;
          response?: { status?: number };
        };

        if (
          err?.code === StatusCode.ERR_NETWORK ||
          err?.code === StatusCode.USER_ACTIVE_STATUS ||
          err?.status === Number(StatusCode.FORBIDDEN)
        ) {
          handleAccountDeactivation();
        }
      }
    }
  };

  useEffect(() => {
    checkStatus();

    return () => {
      // Cleanup timeout on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { checkStatus };
};

export default useAlmCorporateStatus;